import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "moment";
import { includes } from "lodash";
import { isDynamicDate } from "@/services/parameters/DateParameter";
import DateInput from "@/components/DateInput";
import DateTimeInput from "@/components/DateTimeInput";
import DynamicButton from "@/components/dynamic-parameters/DynamicButton";
import notification from "@/services/notification";

import "./DynamicParameters.less";

class DynamicDatePicker extends React.Component {
  static propTypes = {
    type: PropTypes.string,
    className: PropTypes.string,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    parameter: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    onSelect: PropTypes.func,
    dynamicButtonOptions: PropTypes.shape({
      staticValueLabel: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.object,
          label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        })
      ),
    }),
    dateOptions: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  };

  static defaultProps = {
    type: "",
    className: "",
    value: null,
    parameter: null,
    dynamicButtonOptions: {
      options: [],
    },
    onSelect: () => {},
  };

  constructor(props) {
    super(props);
    this.dateComponentRef = React.createRef();
  }

  onDynamicValueSelect = dynamicValue => {
    try {
      const { onSelect, parameter } = this.props;
      if (dynamicValue === "static") {
        const parameterValue = parameter && parameter.getExecutionValue ? parameter.getExecutionValue() : null;
        if (parameterValue) {
          onSelect(moment(parameterValue));
        } else {
          onSelect(null);
        }
      } else {
        // DynamicButton passes option objects. For date presets, actual dynamic object
        // is usually nested as option.value (which itself has a .value() function).
        const dynamicObject =
          dynamicValue && dynamicValue.value && typeof dynamicValue.value.value === "function"
            ? dynamicValue.value
            : dynamicValue;
        const computed = dynamicObject && typeof dynamicObject.value === "function" ? dynamicObject.value() : null;
        if (!computed || !computed.isValid || !computed.isValid()) {
          notification.warning("Invalid dynamic date value");
          return;
        }

        const minDate = parameter?.minDate ? moment(parameter.minDate, moment.ISO_8601, true) : null;
        const maxDate = parameter?.maxDate ? moment(parameter.maxDate, moment.ISO_8601, true) : null;
        if (
          (minDate && minDate.isValid() && computed.isBefore(minDate)) ||
          (maxDate && maxDate.isValid() && computed.isAfter(maxDate))
        ) {
          const minText = minDate && minDate.isValid() ? minDate.format("YYYY-MM-DD HH:mm:ss") : "not set";
          const maxText = maxDate && maxDate.isValid() ? maxDate.format("YYYY-MM-DD HH:mm:ss") : "not set";
          notification.warning(
            "Selected dynamic date is outside allowed range",
            `Allowed slot: ${minText} to ${maxText}.`
          );
          return;
        }

        onSelect(dynamicObject);
      }
    } catch (error) {
      notification.error("Unable to apply dynamic date", String(error));
    } finally {
      // give focus to the DatePicker to get keyboard shortcuts to work
      if (this.dateComponentRef.current && this.dateComponentRef.current.focus) {
        this.dateComponentRef.current.focus();
      }
    }
  };

  render() {
    const { type, value, className, dateOptions, dynamicButtonOptions, onSelect } = this.props;
    const hasDynamicValue = isDynamicDate(value);
    const isDateTime = includes(type, "datetime");

    const additionalAttributes = {};

    let DateComponent = DateInput;
    if (isDateTime) {
      DateComponent = DateTimeInput;
      if (includes(type, "with-seconds")) {
        additionalAttributes.withSeconds = true;
        additionalAttributes.withMinutes = true;
      }

      else if (includes(type, "with-minutes")) {
        additionalAttributes.withMinutes = true;
        additionalAttributes.withSeconds = false;
      }
    }

    if (moment.isMoment(value) || value === null) {
      additionalAttributes.value = value;
    }

    const parameterMinDate = this.props.parameter?.minDate ? moment(this.props.parameter.minDate, moment.ISO_8601, true) : null;
    const parameterMaxDate = this.props.parameter?.maxDate ? moment(this.props.parameter.maxDate, moment.ISO_8601, true) : null;
    const minDate = parameterMinDate && parameterMinDate.isValid() ? parameterMinDate : null;
    const maxDate = parameterMaxDate && parameterMaxDate.isValid() ? parameterMaxDate : null;
    if (minDate || maxDate) {
      additionalAttributes.disabledDate = current => {
        if (!current) return false;
        if (minDate && current.isBefore(minDate, "day")) return true;
        if (maxDate && current.isAfter(maxDate, "day")) return true;
        return false;
      };
    }

    if (hasDynamicValue) {
      const dynamicDate = value;
      additionalAttributes.placeholder = dynamicDate && dynamicDate.name;
      additionalAttributes.value = null;
    }

    return (
      <div className={classNames("date-parameter", className)}>
        <DateComponent
          {...dateOptions}
          ref={this.dateComponentRef}
          className={classNames("redash-datepicker", type, { "dynamic-value": hasDynamicValue })}
          onSelect={onSelect}
          suffixIcon={null}
          {...additionalAttributes}
        />
        <DynamicButton
          options={dynamicButtonOptions.options}
          staticValueLabel={dynamicButtonOptions.staticValueLabel}
          selectedDynamicValue={hasDynamicValue ? value : null}
          enabled={hasDynamicValue}
          onSelect={this.onDynamicValueSelect}
        />
      </div>
    );
  }
}

export default DynamicDatePicker;
